import { useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { MODAL_SKLO, MODAL_POZADI } from '../../lib/uiTokens';
import { SPECIAL_PAGES, zalozkyModulu } from '../../lib/moduly';

/**
 * SearchPalette — Cmd+K modal pro rychlý search napříč moduly a jejich
 * záložkami.
 *
 * Hledat jen moduly znamenalo, že člověk musel vědět, kde věc bydlí:
 * „Docházka" je podstránka Směn, takže na ni hledání neodpovídalo. Záložky
 * proto do nabídky patří taky — je jich 53 proti 20 modulům a jsou to jména,
 * která lidé v provozu používají. Berou se z katalogu (`zalozkyModulu`),
 * takže jsou filtrované podle přístupů stejně jako moduly.
 *
 * Použití cmdk knihovny — built-in fuzzy match, keyboard nav (↑↓/Enter/Esc),
 * accessibility. Modal 480 px wide, centered overlay.
 *
 * Při prázdném inputu zobrazí "Nedávné" — posledních max 5 modulů z
 * `user.sidebar_state.search_recent`. Po výběru:
 *   1. Zavře modal a navigates na modul.
 *   2. Posune ID na začátek `search_recent` (max 5), persistuje přes callback.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Object} props.user
 * @param {Array} props.sekce - Sekce jen s moduly, které uživatel vidí (viditelneSekce)
 * @param {(id: string) => void} props.onNavigate
 * @param {(recent: string[]) => void} props.onUpdateSearchRecent - Persist do sidebar_state
 * @param {boolean} props.isDark
 */
export default function SearchPalette({
    open,
    onClose,
    user,
    sekce,
    onNavigate,
    onUpdateSearchRecent,
    isDark = true,
}) {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setQuery('');
            // Auto-focus po otevření (cmdk to dělá samo, ale safety net)
            setTimeout(() => inputRef.current?.focus?.(), 30);
        }
    }, [open]);

    if (!open) return null;

    const isSuperAdmin = user?.role === 'superadmin';
    const recent = user?.sidebar_state?.search_recent || [];

    const navItems = sekce.flatMap((g) => g.items.map((i) => ({ ...i, group: g, source: 'nav' })));

    // Záložky modulů — otevřou modul rovnou na sobě. Modul bez záložek jich
    // nemá žádné a přeskočí se; připravovaný modul se nenabízí vůbec.
    const zalozkoveItems = sekce.flatMap((g) => g.items
        .filter((i) => !i.pripravovany)
        .flatMap((i) => zalozkyModulu(i.id, user).map((z) => ({
            id: `${i.id}.${z.id}`,
            label: z.nazev,
            popis: i.label,
            Icon: z.Icon,
            group: g,
            source: 'zalozka',
            page: i.id,
            params: { tab: z.id },
        }))));

    // Special pages — Profil každému, Média jen adminům, kds-test jen
    // superadminu. Nastavení je modul Správa a hledá se jako modul.
    const isAdmin = isSuperAdmin || user?.role === 'admin';
    const specialItems = Object.entries(SPECIAL_PAGES)
        .filter(([id]) => {
            if (id === 'kds-test') return isSuperAdmin;
            if (id === 'media') return isAdmin;
            return id === 'profil';
        })
        .map(([id, meta]) => ({ id, ...meta, source: 'special' }));

    const allItems = [...navItems, ...zalozkoveItems, ...specialItems];

    // Recent items — vyhledat metadata, filtrovat na ty, co user pořád vidí.
    const recentItems = recent
        .map((id) => allItems.find((i) => i.id === id))
        .filter(Boolean)
        .slice(0, 5);

    const handleSelect = (item) => {
        if (!item?.id) return;
        // Posun ID na začátek search_recent (max 5).
        const newRecent = [item.id, ...recent.filter((r) => r !== item.id)].slice(0, 5);
        onUpdateSearchRecent?.(newRecent);
        // Záložka otevře svůj modul rovnou na sobě; modul se otevře, jak je.
        onNavigate?.(item.page ?? item.id, item.params);
        onClose?.();
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    ...MODAL_POZADI,
                    zIndex: 9998,
                    animation: 'cmdk-backdrop-in 0.15s ease-out',
                }}
            />
            <style>{`
                @keyframes cmdk-backdrop-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes cmdk-modal-in {
                    from { transform: translate(-50%, -50%) scale(0.96); opacity: 0; }
                    to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }
                .fl-cmdk [cmdk-input] {
                    width: 100%;
                    padding: 14px 14px 14px 42px;
                    background: transparent;
                    border: none;
                    color: var(--text-primary);
                    font-size: 15px;
                    outline: none;
                    box-sizing: border-box;
                }
                .fl-cmdk [cmdk-input]::placeholder {
                    color: var(--text-muted);
                }
                .fl-cmdk [cmdk-list] {
                    max-height: 360px;
                    overflow-y: auto;
                    padding: 6px;
                }
                .fl-cmdk [cmdk-group-heading] {
                    padding: 8px 10px 4px;
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: ${isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)'};
                }
                .fl-cmdk [cmdk-item] {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 9px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    color: var(--text-primary);
                    font-size: 13.5px;
                }
                .fl-cmdk [cmdk-item][data-selected="true"] {
                    background: ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
                }
                .fl-cmdk [cmdk-empty] {
                    padding: 24px 16px;
                    text-align: center;
                    font-size: 12px;
                    color: ${isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)'};
                }
            `}</style>

            <div
                role="dialog"
                aria-label="Hledat moduly"
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(480px, 92vw)',
                    ...MODAL_SKLO,
                    zIndex: 9999,
                    animation: 'cmdk-modal-in 0.16s ease-out',
                    overflow: 'hidden',
                }}
                className="fl-cmdk"
            >
                <Command shouldFilter={query.length > 0} loop>
                    {/* Search ikona + input */}
                    <div style={{
                        position: 'relative',
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                    }}>
                        <Search
                            size={16}
                            style={{
                                position: 'absolute',
                                left: 14,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)',
                                pointerEvents: 'none',
                            }}
                        />
                        <Command.Input
                            ref={inputRef}
                            value={query}
                            onValueChange={setQuery}
                            placeholder="Hledat modul nebo záložku…"
                        />
                    </div>

                    <Command.List>
                        <Command.Empty>Nic takového tu není.</Command.Empty>

                        {/* Recent — jen když prázdný input */}
                        {!query && recentItems.length > 0 && (
                            <Command.Group heading="Nedávné">
                                {recentItems.map((item) => (
                                    <CmdItem
                                        key={`recent-${item.id}`}
                                        item={item}
                                        onSelect={() => handleSelect(item)}
                                    />
                                ))}
                            </Command.Group>
                        )}

                        {/* Všechny moduly (cmdk filtr aplikuje fuzzy match při query) */}
                        {/* Bez dotazu stačí moduly — 53 záložek pod nimi by
                            z nabídky udělalo seznam, který se musí projít.
                            Jakmile se píše, hledá se ve všem. */}
                        <Command.Group heading={query ? 'Výsledky' : 'Všechny moduly'}>
                            {(query ? allItems : navItems).map((item) => (
                                <CmdItem
                                    key={item.id}
                                    item={item}
                                    onSelect={() => handleSelect(item)}
                                />
                            ))}
                        </Command.Group>
                    </Command.List>
                </Command>
            </div>
        </>
    );
}

function CmdItem({ item, onSelect }) {
    const Icon = item.Icon;
    const accent = item.group?.color ?? item.color ?? '#6B7280';
    return (
        <Command.Item
            value={`${item.label} ${item.id}`}
            onSelect={onSelect}
            keywords={[item.label, item.id, item.popis, item.group?.label].filter(Boolean)}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: `${accent}1a`, color: accent,
                }}
            >
                <Icon size={14} strokeWidth={2} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
                {item.label}
                {item.popis && (
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{item.popis}</span>
                )}
            </span>
            {item.group && (
                <span style={{ fontSize: 10, opacity: 0.5 }}>{item.group.label}</span>
            )}
        </Command.Item>
    );
}
